import { type MaintenanceWorkOrder } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import {
  usePurgeMaintenanceWorkOrder,
  useRestoreTrashedMaintenanceWorkOrder,
  useTrashMaintenanceWorkOrder,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import { maintenanceWorkOrderEditPath } from '@/products/maintenance/maintenance-paths';

type OrderActionFields = Pick<MaintenanceWorkOrder, 'id' | 'orderNumber' | 'status' | 'trashedAt'>;

const EDITABLE = new Set<MaintenanceWorkOrder['status']>([
  'planned',
  'assigned',
  'in_progress',
  'finalizing',
]);

const TRASH_HINT =
  'Der Auftrag wird aus Planung, Archiv und der Monteur-Liste genommen. Im Papierkorb kann er wiederhergestellt oder endgültig gelöscht werden.';

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}

export function MaintenanceWorkOrderActions({
  order,
  onTrashed,
  onPurged,
}: {
  order: OrderActionFields;
  onTrashed?: () => void;
  onPurged?: () => void;
}) {
  const trash = useTrashMaintenanceWorkOrder();
  const restore = useRestoreTrashedMaintenanceWorkOrder();
  const purge = usePurgeMaintenanceWorkOrder();
  const [mode, setMode] = useState<'trash' | 'restore' | 'purge' | null>(null);
  const pending = trash.isPending || restore.isPending || purge.isPending;
  const inTrash = order.trashedAt != null;

  async function confirm() {
    if (!mode) {
      return;
    }
    try {
      if (mode === 'trash') {
        await trash.mutateAsync(order.id);
        toast.success('Auftrag in den Papierkorb gelegt.');
        setMode(null);
        onTrashed?.();
        return;
      }
      if (mode === 'restore') {
        await restore.mutateAsync(order.id);
        toast.success('Auftrag wiederhergestellt.');
        setMode(null);
        return;
      }
      await purge.mutateAsync(order.id);
      toast.success('Auftrag endgültig gelöscht.');
      setMode(null);
      onPurged?.();
    } catch (error: unknown) {
      toast.error(messageOf(error, 'Die Aktion ist fehlgeschlagen.'));
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {!inTrash && EDITABLE.has(order.status) ? (
        <Button asChild variant="outline" className="min-h-11" size="sm">
          <Link to={maintenanceWorkOrderEditPath(order.id)}>Bearbeiten</Link>
        </Button>
      ) : null}
      {inTrash ? (
        <>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            size="sm"
            onClick={() => setMode('restore')}
          >
            Wiederherstellen
          </Button>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            size="sm"
            onClick={() => setMode('purge')}
          >
            Endgültig löschen
          </Button>
        </>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          size="sm"
          onClick={() => setMode('trash')}
        >
          In Papierkorb
        </Button>
      )}
      <AlertDialog
        open={mode != null}
        onOpenChange={(next) => {
          if (!next && !pending) {
            setMode(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mode === 'purge'
                ? `Auftrag ${order.orderNumber} endgültig löschen?`
                : mode === 'restore'
                  ? `Auftrag ${order.orderNumber} wiederherstellen?`
                  : `Auftrag ${order.orderNumber} in den Papierkorb legen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mode === 'purge'
                ? 'Der Auftrag und alle Dateien werden unwiderruflich gelöscht.'
                : mode === 'restore'
                  ? 'Der Auftrag erscheint wieder in Planung oder Archiv und in der Liste des Monteurs.'
                  : TRASH_HINT}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending || mode == null}
              onClick={(event) => {
                event.preventDefault();
                void confirm();
              }}
            >
              {mode === 'purge'
                ? 'Endgültig löschen'
                : mode === 'restore'
                  ? 'Wiederherstellen'
                  : 'In Papierkorb'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
